/**
 * Ideas — `/ideas` (members and requesters: suggest and track) and `/team/ideas` (founder and Ops associate
 * director: decide). Prototype VIEWS.ideas, newIdea, openIdea, ideaAct, ideaMove, askProof('idea'), i_ok, ideaReply
 * (docs/prototype.html L1690–L1758), with the hub-mode head() rule (L755: in a hub tab the page head belongs to the
 * hub, the view keeps only its button, and `.hubbody .stats` is hidden).
 *
 * Data (packages/contracts ideas.ts, every route agreed):
 *  - GET /ideas?stage= → the cards, the four tiles (`stats`) and the pill counts (`stage_counts`);
 *  - GET /ideas/{id} → the drawer (trail, proof);
 *  - POST /ideas → "Suggest an idea" (requesters get no effort field; the server defaults it to M);
 *  - POST /ideas/{id}/decide → Approve (who builds it, target date) / Ask for detail / Decline;
 *  - POST /ideas/{id}/reply → "Add the detail asked for";
 *  - POST /ideas/{id}/log → Start building / Mark live, with proof / Result looks right.
 *
 * Proof is a link or a note: no upload route exists for idea proof (the API refuses a file key, as for checklist
 * proof), so there is no file input that would go nowhere.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { IDEA_EFFORT_LABEL, IdeaEffort, isRequesterRole, type IdeaStage } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../../api';
import { useClients, useUsers } from '../../lib/data';
import { firstName, todayIso } from '../../lib/format';
import { IDEA_STAGES, ideaChip, shortDate, useIdea, useIdeaWrite, useIdeas, whenLabel, type Idea } from '../../lib/ideas';
import { useRequestOptions } from '../../lib/requests';
import { useSession } from '../../lib/session';
import { PageInfo } from '../../shell/Help';
import { Drawer, Empty, ErrorState, Field, Loading, Modal, Pills, errorText, useFail, useToast } from '../../ui/kit';
import { Stat } from '../timesheets/TimesheetReport';

const RO_TITLE = 'Read-only while you view as someone';
const EFFORTS = IdeaEffort.options;
/** Who may build an idea (the API's BUILDERS): active founder, Ops associate director and Ops members. */
const BUILDER_ROLES = ['founder', 'ops_ad', 'ops_member'];

type Close = () => void;
type Act = 'approve' | 'needs_info' | 'decline';

// ================================================================ the screen

export function Ideas({ hub = false }: { hub?: boolean }) {
  const { can, readOnly } = useSession();
  const decider = can('ideas.decide');
  const [params, setParams] = useSearchParams();
  // Prototype ID.f = 'auto': deciders open on what waits for them, everyone else on all of theirs.
  const [stage, setStage] = useState<IdeaStage>(decider ? 'wait' : 'all');
  const [adding, setAdding] = useState(false);
  const q = useIdeas(stage);
  const openId = params.get('idea');
  const setOpen = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('idea', id);
        else n.delete('idea');
        return n;
      },
      { replace: true },
    );

  const suggest = can('ideas.submit') ? (
    <button className="btn pri" onClick={() => setAdding(true)} disabled={readOnly} title={readOnly ? RO_TITLE : undefined}>
      + Suggest an idea
    </button>
  ) : null;

  const body = () => {
    if (q.isPending) return <Loading label="Loading ideas…" />;
    if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
    const { data, stats, stage_counts: n } = q.data;
    return (
      <>
        {hub ? null : (
          <div className="stats">
            <Stat k="Suggested" v={stats.suggested} />
            <Stat k="Waiting for a decision" v={stats.waiting} cls={stats.waiting ? 'amber' : ''} />
            <Stat k="Live" v={stats.live} cls="green" />
            <Stat k="Approval rate" v={stats.approval_rate_pct === null ? '-' : `${Math.round(stats.approval_rate_pct)}%`} s="of decided ideas" />
          </div>
        )}
        <div className="bar">
          <Pills<IdeaStage> options={[['all', 'All', n.all ?? 0], ...IDEA_STAGES.map(([k, l]) => [k, l, n[k] ?? 0] as [IdeaStage, string, number])]} value={stage} onChange={setStage} />
        </div>
        <div style={{ display: 'grid', gap: 10, opacity: q.isPlaceholderData ? 0.6 : 1 }} aria-busy={q.isFetching}>
          {data.length ? (
            data.map((i) => <IdeaCard key={i.id} i={i} onOpen={() => setOpen(i.id)} />)
          ) : (
            <div className="panel">
              <Empty title="Nothing here">Pick another stage, or suggest an idea.</Empty>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      {hub ? (
        suggest ? (
          <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
            <div className="r" style={{ marginLeft: 0 }}>
              {suggest}
            </div>
          </div>
        ) : null
      ) : (
        <div className="ph">
          <div>
            <h1>
              Ideas
              <PageInfo helpKey="ideas" />
            </h1>
            <p>Anyone can suggest an improvement. The Ops associate director decides, and we track what actually ships.</p>
          </div>
          <div className="r">{suggest}</div>
        </div>
      )}
      {body()}
      {adding ? (
        <SuggestModal
          onClose={() => setAdding(false)}
          onCreated={() => {
            // Prototype saveIdea: ID.f = 'all', so the new idea is in view whatever the pill was.
            setStage('all');
          }}
        />
      ) : null}
      {openId ? <IdeaDrawer id={openId} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function IdeaCard({ i, onOpen }: { i: Idea; onOpen: () => void }) {
  const today = todayIso();
  const st = ideaChip(i.status);
  return (
    <div
      className="panel click"
      style={{ cursor: 'pointer' }}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="bd" style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="grow" style={{ minWidth: 260, flex: 1 }}>
          <div style={{ fontWeight: 650, fontSize: 14.5, overflowWrap: 'anywhere' }}>{i.title}</div>
          <div className="sub" style={{ marginTop: 4, overflowWrap: 'anywhere' }}>
            {i.benefit}
          </div>
          <div className="m" style={{ marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, color: 'var(--sub)' }}>
            <span>{i.client ? i.client.name : 'All clients'}</span>
            <span>
              by {firstName(i.by.name)}, {whenLabel(i.created_at, today)}
            </span>
            <span>Effort {i.effort}</span>
            {i.owner ? (
              <span>
                Owner {firstName(i.owner.name)}
                {i.target_on ? `, target ${shortDate(i.target_on)}` : ''}
              </span>
            ) : null}
          </div>
        </div>
        <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
          <span className={`chip ${st.tone}`}>{st.label}</span>
          {i.status === 'live' ? <span className={`chip ${i.proof?.checked ? 'hot' : ''}`}>{i.proof?.checked ? 'Result checked' : 'Result to check'}</span> : null}
        </div>
      </div>
    </div>
  );
}

// ================================================================ the drawer

type Pop = { kind: 'decide'; act: Act } | { kind: 'live' } | { kind: 'reply' } | null;

function IdeaDrawer({ id, onClose }: { id: string; onClose: Close }) {
  const { me, can, readOnly } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const q = useIdea(id);
  const [pop, setPop] = useState<Pop>(null);
  const decider = can('ideas.decide');

  const step = useIdeaWrite((v: { i: Idea; step: 'building' | 'result_checked' }) =>
    call('ideas.log', { params: { id: v.i.id }, ifMatch: v.i.version, idempotencyKey: newIdempotencyKey(), body: { step: v.step } }),
  );
  const doStep = (i: Idea, s: 'building' | 'result_checked') =>
    step.mutate(
      { i, step: s },
      {
        onSuccess: () => {
          toast(s === 'building' ? 'Now being built' : 'Result accepted');
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );

  const i = q.data;
  const own = !!i && i.by.id === me.user.id;
  const ro = readOnly ? { disabled: true, title: RO_TITLE } : {};
  const requester = isRequesterRole(me.user.role);

  const actions = i ? (
    <>
      {decider && (i.status === 'new' || i.status === 'needs_info') ? (
        <>
          <button className="btn good" {...ro} onClick={() => setPop({ kind: 'decide', act: 'approve' })}>
            Approve
          </button>
          <button className="btn" {...ro} onClick={() => setPop({ kind: 'decide', act: 'needs_info' })}>
            Ask for detail
          </button>
          <button className="btn bad" {...ro} onClick={() => setPop({ kind: 'decide', act: 'decline' })}>
            Decline
          </button>
        </>
      ) : null}
      {decider && i.status === 'approved' ? (
        <button className="btn pri" disabled={readOnly || step.isPending} title={readOnly ? RO_TITLE : undefined} onClick={() => doStep(i, 'building')}>
          {step.isPending ? 'Starting…' : 'Start building'}
        </button>
      ) : null}
      {i.status === 'building' && (decider || i.owner?.id === me.user.id) ? (
        <button className="btn good" {...ro} onClick={() => setPop({ kind: 'live' })}>
          Mark live, with proof
        </button>
      ) : null}
      {i.status === 'live' && decider && i.proof && !i.proof.checked ? (
        <button
          className="btn good"
          disabled={readOnly || step.isPending || i.proof.by.id === me.user.id}
          title={readOnly ? RO_TITLE : i.proof.by.id === me.user.id ? 'You marked this live, so someone else checks the result.' : undefined}
          onClick={() => doStep(i, 'result_checked')}
        >
          Result looks right
        </button>
      ) : null}
      {i.status === 'needs_info' && own ? (
        <button className="btn pri" {...ro} onClick={() => setPop({ kind: 'reply' })}>
          Add the detail asked for
        </button>
      ) : null}
    </>
  ) : null;

  const st = i ? ideaChip(i.status) : null;
  const clientHref = i?.client ? `/clients/book?client=${encodeURIComponent(i.client.id)}` : '';

  return (
    <Drawer
      title="Idea"
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {q.isPending ? (
        <Loading label="Loading the idea…" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : i && st ? (
        <>
          <h2 style={{ fontSize: 18, marginBottom: 10, overflowWrap: 'anywhere' }}>{i.title}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <span className={`chip ${st.tone}`}>{st.label}</span>
            <span className="chip">Effort {i.effort}</span>
            {i.status === 'live' ? <span className={`chip ${i.proof?.checked ? 'hot' : ''}`}>{i.proof?.checked ? 'Result checked' : 'Result to check'}</span> : null}
          </div>
          <dl className="kv">
            <dt>From</dt>
            <dd>
              {i.by.name}, {whenLabel(i.created_at, todayIso())}
            </dd>
            <dt>Client</dt>
            <dd>
              {i.client ? (
                requester ? (
                  i.client.name
                ) : (
                  <a
                    href={clientHref}
                    onClick={(e) => {
                      e.preventDefault();
                      nav(clientHref);
                    }}
                    style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}
                  >
                    {i.client.name}
                  </a>
                )
              ) : (
                'All clients'
              )}
            </dd>
            <dt>The idea</dt>
            <dd style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{i.what || '-'}</dd>
            <dt>What we gain</dt>
            <dd style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{i.benefit}</dd>
            {i.owner ? (
              <>
                <dt>Owner</dt>
                <dd>
                  {i.owner.name}
                  {i.target_on ? `, target ${shortDate(i.target_on)}` : ''}
                </dd>
              </>
            ) : null}
            {i.note ? (
              <>
                <dt>Manager note</dt>
                <dd style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{i.note}</dd>
              </>
            ) : null}
            {i.result ? (
              <>
                <dt>Result</dt>
                <dd style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{i.result}</dd>
              </>
            ) : null}
          </dl>
          {i.proof ? (
            <>
              <div className="lbl" style={{ margin: '18px 0 8px' }}>
                Proof it is live
              </div>
              <IdeaProofBlock i={i} />
            </>
          ) : null}
          <div className="lbl" style={{ margin: '18px 0 8px' }}>
            Trail
          </div>
          <div style={{ fontSize: 13, color: 'var(--sub)', display: 'grid', gap: 5 }}>
            {i.log.map((l, n) => (
              <div key={n}>
                {whenLabel(l.at, todayIso())}: {l.text}
              </div>
            ))}
          </div>
          {/* Prototype openIdea: the actions sit under the trail and wrap, so four of them never overflow the drawer. */}
          <div className="split" style={{ marginTop: 22, flexWrap: 'wrap' }}>
            {actions}
          </div>
        </>
      ) : null}
      {i && pop?.kind === 'decide' ? <DecideModal i={i} act={pop.act} onClose={() => setPop(null)} onDone={onClose} /> : null}
      {i && pop?.kind === 'live' ? <LiveModal i={i} onClose={() => setPop(null)} onDone={onClose} /> : null}
      {i && pop?.kind === 'reply' ? <ReplyModal i={i} onClose={() => setPop(null)} onDone={onClose} /> : null}
    </Drawer>
  );
}

/** Prototype proofBlock() for an idea: the link or "No attachment needed", the result line, and whether it is checked. */
function IdeaProofBlock({ i }: { i: Idea }) {
  const p = i.proof!;
  return (
    <div className="note" style={{ margin: 0, display: 'grid', gap: 6 }}>
      <div>
        <b>Proof of closure</b> from {p.by.name}, {whenLabel(p.at, todayIso())}
      </div>
      {p.proof_url ? (
        <a href={p.proof_url} target="_blank" rel="noreferrer" className="chip brand" style={{ justifySelf: 'start', textDecoration: 'none', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.proof_url.replace(/^https?:\/\//, '').slice(0, 60)}
        </a>
      ) : p.no_attachment_needed ? (
        <span className="sub">No attachment needed</span>
      ) : null}
      {p.proof_text ? <div style={{ overflowWrap: 'anywhere' }}>{p.proof_text}</div> : null}
      <div>
        {p.checked ? (
          <span className="chip hot">Checked by {p.checked_by ? firstName(p.checked_by.name) : 'the Ops associate director'}</span>
        ) : (
          <span className="chip warm">Not checked yet</span>
        )}
      </div>
    </div>
  );
}

// ================================================================ pop-ups

function Foot({ onClose, cancel = 'Cancel', label, tone = 'pri', busy, onGo }: { onClose: Close; cancel?: string; label: string; tone?: string; busy: boolean; onGo: () => void }) {
  return (
    <>
      <div className="grow" />
      <button className="btn" onClick={onClose}>
        {cancel}
      </button>
      <button className={`btn ${tone}`} onClick={onGo} disabled={busy} aria-busy={busy}>
        {label}
      </button>
    </>
  );
}

/** Prototype newIdea / saveIdea. */
function SuggestModal({ onClose, onCreated }: { onClose: Close; onCreated: () => void }) {
  const { me } = useSession();
  const requester = isRequesterRole(me.user.role);
  // Requesters cannot read /clients; the request form's options carry every active client by name.
  const clients = useClients(!requester);
  const options = useRequestOptions(requester);
  const list = requester ? (options.data?.clients ?? []) : (clients.data ?? []);
  const listQ = requester ? options : clients;
  const { errs, setErrs, fail, toast } = useFail(['title', 'what', 'benefit', 'client_id', 'effort']);
  const [title, setTitle] = useState('');
  const [what, setWhat] = useState('');
  const [benefit, setBenefit] = useState('');
  const [clientId, setClientId] = useState('');
  const [effort, setEffort] = useState<IdeaEffort>('M');
  const [key] = useState(newIdempotencyKey);

  const create = useIdeaWrite(() =>
    call('ideas.create', {
      idempotencyKey: key,
      body: {
        title: title.trim(),
        what: what.trim() || undefined,
        benefit: benefit.trim(),
        client_id: clientId || undefined,
        effort: requester ? undefined : effort,
      },
    }),
  );

  const save = () => {
    setErrs({});
    if (!title.trim() || !benefit.trim()) return toast('Add the idea and what we gain');
    create.mutate(undefined, {
      onSuccess: () => {
        toast('Sent. The Ops associate director will decide.');
        onCreated();
        onClose();
      },
      onError: fail,
    });
  };

  return (
    <Modal
      title="Suggest an idea"
      sub="Something that would make a client happier, save the team time, or avoid a repeat problem."
      onClose={onClose}
      footer={<Foot onClose={onClose} label={create.isPending ? 'Sending…' : 'Send for a decision'} busy={create.isPending} onGo={save} />}
    >
      <Field label="The idea in one line" error={errs.title}>
        <input className="in" id="niT" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Check templates against Meta rules before submitting" autoFocus />
      </Field>
      <Field label="What would we do?" error={errs.what}>
        <textarea className="in" id="niW" rows={2} value={what} maxLength={2000} onChange={(e) => setWhat(e.target.value)} />
      </Field>
      <Field label="What do we gain?" error={errs.benefit}>
        <textarea className="in" id="niB" rows={2} value={benefit} maxLength={2000} onChange={(e) => setBenefit(e.target.value)} placeholder="Time saved, fewer escalations, more revenue" />
      </Field>
      <div className={requester ? '' : 'cols c2'}>
        <Field label="For which client" error={errs.client_id}>
          {listQ.isError ? (
            <div className="ferr">{errorText(listQ.error)}</div>
          ) : (
            <select className="in" id="niC" value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={listQ.isPending}>
              <option value="">{listQ.isPending ? 'Loading…' : 'All clients'}</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        {requester ? null : (
          <Field label="Effort" error={errs.effort}>
            <select className="in" id="niE" value={effort} onChange={(e) => setEffort(e.target.value as IdeaEffort)}>
              {EFFORTS.map((k) => (
                <option key={k} value={k}>
                  {IDEA_EFFORT_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>
    </Modal>
  );
}

/** Prototype ideaAct / saveIdeaAct: approve (who builds it, target date), ask for detail, or decline. */
function DecideModal({ i, act, onClose, onDone }: { i: Idea; act: Act; onClose: Close; onDone: Close }) {
  const { can } = useSession();
  const users = useUsers(act === 'approve' && can('users.read'));
  const builders = (users.data ?? []).filter((u) => u.status === 'active' && BUILDER_ROLES.includes(u.role));
  const { errs, setErrs, fail, toast } = useFail(['owner_id', 'target_on', 'note']);
  const [ownerId, setOwnerId] = useState('');
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const [key] = useState(newIdempotencyKey);
  const author = firstName(i.by.name);
  // Prototype: the author builds it by default, when they can.
  const owner = ownerId || (builders.some((u) => u.id === i.by.id) ? i.by.id : builders[0]?.id) || '';

  const save = useIdeaWrite(() =>
    call('ideas.decide', {
      params: { id: i.id },
      ifMatch: i.version,
      idempotencyKey: key,
      body: act === 'approve' ? { decision: 'approve', owner_id: owner, target_on: target, note: note.trim() || undefined } : { decision: act, note: note.trim() },
    }),
  );

  const go = () => {
    setErrs({});
    if (act === 'approve') {
      if (!owner) return toast('Pick who builds it');
      if (!target) return toast('Set a target date');
    } else if (!note.trim()) return toast(`Add a note so ${author} knows why`);
    save.mutate(undefined, {
      onSuccess: () => {
        toast(act === 'approve' ? 'Approved' : act === 'needs_info' ? 'Question sent' : 'Declined');
        onClose();
        onDone();
      },
      onError: fail,
    });
  };

  const title = act === 'approve' ? 'Approve this idea' : act === 'needs_info' ? 'Ask for more detail' : 'Decline this idea';
  const label = act === 'approve' ? 'Approve' : act === 'needs_info' ? 'Send question' : 'Decline';
  return (
    <Modal title={title} sub={i.title} onClose={onClose} footer={<Foot onClose={onClose} label={save.isPending ? 'Saving…' : label} tone={act === 'decline' ? 'bad' : 'pri'} busy={save.isPending} onGo={go} />}>
      {act === 'approve' ? (
        <div className="cols c2">
          <Field label="Who builds it" error={errs.owner_id}>
            {users.isError ? (
              <div className="ferr">{errorText(users.error)}</div>
            ) : (
              <select className="in" id="iaO" value={owner} onChange={(e) => setOwnerId(e.target.value)} disabled={users.isPending}>
                {users.isPending ? <option>Loading…</option> : null}
                {builders.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Target date" error={errs.target_on}>
            <input className="in" id="iaT" type="date" value={target} min={todayIso()} onChange={(e) => setTarget(e.target.value)} />
          </Field>
        </div>
      ) : null}
      <Field label={act === 'approve' ? `Note for ${author} (optional)` : act === 'needs_info' ? 'What do you need to know?' : `Why not? ${author} will see this`} error={errs.note}>
        <textarea className="in" id="iaN" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

/** Prototype askProof('idea') / saveProof: a link or "no attachment needed", and what was done and what changed. */
function LiveModal({ i, onClose, onDone }: { i: Idea; onClose: Close; onDone: Close }) {
  const { errs, fail, toast } = useFail(['proof_url', 'result', 'proof_file_key']);
  const [link, setLink] = useState('');
  const [none, setNone] = useState(false);
  const [result, setResult] = useState('');
  const [local, setLocal] = useState('');
  const [key] = useState(newIdempotencyKey);
  const url = none ? '' : link.trim();

  const save = useIdeaWrite(() =>
    call('ideas.log', {
      params: { id: i.id },
      ifMatch: i.version,
      idempotencyKey: key,
      body: url
        ? { step: 'live', result: result.trim(), proof_kind: 'link', proof_url: url }
        : { step: 'live', result: result.trim(), proof_kind: 'note', no_attachment_needed: true },
    }),
  );

  const go = () => {
    if (!url && !none) return setLocal('Paste a link, or tick No attachment needed');
    if (result.trim().length < 3) return setLocal('Say in a line what was done');
    setLocal('');
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Closed with proof');
        onClose();
        onDone();
      },
      onError: fail,
    });
  };

  return (
    <Modal title="Show it is done" onClose={onClose} footer={<Foot onClose={onClose} cancel="Not yet" label={save.isPending ? 'Closing…' : 'Close with proof'} tone="good" busy={save.isPending} onGo={go} />}>
      <p style={{ marginTop: 0 }}>Before this idea closes, attach something that proves it: the email you sent, the report, or a link. The Ops associate director checks it.</p>
      <p className="sub" style={{ marginTop: -6 }}>
        <b>{i.title}</b>
      </p>
      <Field label="Paste a link" error={errs.proof_url ?? errs.proof_file_key}>
        <input className="in" id="pfLink" value={link} disabled={none} onChange={(e) => setLink(e.target.value)} placeholder="e.g. link to the email thread or shared folder" inputMode="url" />
      </Field>
      <label className="split" style={{ margin: '2px 0 10px', alignItems: 'flex-start' }}>
        <input type="checkbox" id="pfNo" checked={none} onChange={(e) => setNone(e.target.checked)} style={{ marginTop: 4 }} />
        <span style={{ fontSize: 13 }}>
          <b>No attachment needed for this idea.</b> <span className="sub">Close it with a one-line note instead, for example when the work was a call or a quick check.</span>
        </span>
      </label>
      <Field label="What was done and what changed" error={errs.result}>
        <textarea className="in" id="pfNote" rows={3} value={result} maxLength={2000} onChange={(e) => setResult(e.target.value)} placeholder="e.g. Live for all BFSI clients from 1 Oct. Incidents fell from 3 to 0." />
      </Field>
      {local ? (
        <div className="ferr" role="alert">
          {local}
        </div>
      ) : null}
    </Modal>
  );
}

/** Prototype ideaReply / saveReply: the author answers, and the idea goes back for a decision. */
function ReplyModal({ i, onClose, onDone }: { i: Idea; onClose: Close; onDone: Close }) {
  const { errs, setErrs, fail, toast } = useFail(['note']);
  const [note, setNote] = useState('');
  const [key] = useState(newIdempotencyKey);
  const save = useIdeaWrite(() => call('ideas.reply', { params: { id: i.id }, ifMatch: i.version, idempotencyKey: key, body: { note: note.trim() } }));
  const go = () => {
    setErrs({});
    if (!note.trim()) return toast('Write an answer');
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Sent back for a decision');
        onClose();
        onDone();
      },
      onError: fail,
    });
  };
  return (
    <Modal
      title="Add the detail"
      sub={
        <>
          {i.title}
          {i.note ? (
            <>
              <br />
              <span className="sub">Asked: {i.note}</span>
            </>
          ) : null}
        </>
      }
      onClose={onClose}
      footer={<Foot onClose={onClose} label={save.isPending ? 'Sending…' : 'Send'} busy={save.isPending} onGo={go} />}
    >
      <Field label="Your answer" error={errs.note}>
        <textarea className="in" id="irN" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}
